<script lang="ts">
  import { getContext } from "svelte"
  import ModuleTypes from "../constants/ModuleTypes"
  import IntegrationTypes from "../constants/IntegrationTypes"
  import { buildUrlData } from "../lib/buildModuleUrlData"
  import Button from "../../component_library/components/svelte/Button.svelte"
  import {
    getFlowTypeFromModuleType,
    productTypeForModuleType,
  } from "../lib/Helpers"
  import type { Theme } from "website_script/types/theme"
  import type { Settings } from "website_script/types/settings"
  import type { Environment } from "website_script/types/environment"
  import type { ElementData } from "website_script/lib/Parsers"
  import { getFlowsContext } from "website_script/contexts/flows"
  import { ButtonConfig } from "website_script/types/widgets"

  const settings = getContext<Settings>("settings")
  const { abTesting, trackFunction } = getContext<Environment>("environment")
  const { accentColor, textCtaColor } = getContext<Theme>("theme")

  /** Trigger for the root modal */
  export let buttonConfig: ButtonConfig
  export let elementData: ElementData

  const { triggerFlow } = getFlowsContext()

  type SupportedModuleTypes =
    | ModuleTypes.TRADE_IN
    | ModuleTypes.PRICE_ALERT
    | ModuleTypes.CUSTOM
    | ModuleTypes.IMPROVED_BOOK_TEST_DRIVE
    | ModuleTypes.BOOK_TEST_DRIVE
  export let moduleType: SupportedModuleTypes = ModuleTypes.TRADE_IN

  export let leadFlowId: number | undefined = undefined

  /**
   * Determines the integration type based on the module type
   */
  const getIntegrationType = (
    moduleType: SupportedModuleTypes
  ): IntegrationTypes => {
    switch (moduleType) {
      case ModuleTypes.TRADE_IN:
        return IntegrationTypes.TRADE_IN_INLINE_BUTTON
      case ModuleTypes.PRICE_ALERT:
        return IntegrationTypes.PRICE_ALERT_INLINE_BUTTON
      case ModuleTypes.BOOK_TEST_DRIVE:
      case ModuleTypes.IMPROVED_BOOK_TEST_DRIVE:
        return IntegrationTypes.TEST_DRIVE_INLINE_BUTTON
      case ModuleTypes.CUSTOM:
        return IntegrationTypes.CUSTOM
    }
  }

  const integrationType = getIntegrationType(moduleType)
  const trackType = productTypeForModuleType(moduleType)

  const urlBuildData = buildUrlData({
    settings,
    moduleType,
    integrationType,
    abTesting: $abTesting,
    departmentUuid: elementData.departmentId,
    elData: elementData,
    leadFlowId,
  })

  const flow = getFlowTypeFromModuleType(moduleType)

  /**
   * Tracks the click and shows the modal with the flow loaded
   */
  const clickHandler = () => {
    if ([ModuleTypes.TRADE_IN, ModuleTypes.PRICE_ALERT].includes(moduleType)) {
      if (flow) {
        trackFunction({
          entity: "inline_button",
          element: moduleType,
          action: "clicked",
          type: "interacted_with_cta",
          flow,
        })
      }
    } else {
      trackFunction(
        `Clicked ${moduleType} inline button`,
        "interacted_with_cta",
        trackType
      )
    }
    triggerFlow(buttonConfig, integrationType, urlBuildData, elementData)
  }

  if (flow) {
    trackFunction({
      entity: "inline_button",
      element: moduleType,
      action: "displayed",
      type: "displayed_cta",
      flow,
    })
  }
</script>

<!--
@component

Creates a button that can show a modal with the TradeIn or Test drive flow shown.
-->
{#if buttonConfig}
  <Button
    {clickHandler}
    style={`background-color: ${accentColor}; color: ${textCtaColor}`}
  >
    {buttonConfig.text}
  </Button>
{/if}
